import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { labResultsService } from '../../services/labResultsService';
import { patientService } from '../../services/patientService';
import { FaSync, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const LabResultsForm = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const appointmentId = patientId;

  const [loading, setLoading] = useState(true);
  const [labResults, setLabResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingLabResults, setLoadingLabResults] = useState(false);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  
  // Blood report generation state
  const [isNotifying, setIsNotifying] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [includeAbnormalBiomarkers, setIncludeAbnormalBiomarkers] = useState(true);
  const [includeResultsAtGlance, setIncludeResultsAtGlance] = useState(true);

  const HEALTH_CATEGORIES = [
    { id: 1, name: 'Metabolic Health' },
    { id: 2, name: 'Heart Health' },
    { id: 3, name: 'Cognition and Memory' },
    { id: 4, name: 'Endurance and Fitness' },
    { id: 5, name: 'Immune Health' },
    { id: 6, name: 'Gut Health' },
    { id: 7, name: 'Inflammation' },
    { id: 8, name: 'Bone Health' },
    { id: 9, name: 'Female Hormones' },
    { id: 10, name: 'Male Hormones' },
    { id: 11, name: 'Sleep and Recovery' }
  ];

  // Mapping of test codes to health category IDs
  const TEST_CODE_TO_CATEGORY = {
    'P1942': 1,   // Metabolic Health
    'P11942': 1,  // Metabolic Health (alternate format)
    'P1943': 2,   // Heart Health
    'P11943': 2,  // Heart Health (alternate format)
    'P1944': 3,   // Cognition and Memory
    'P11944': 3,  // Cognition and Memory (alternate format)
    'P1945': 4,   // Endurance and Fitness
    'P11945': 4,  // Endurance and Fitness (alternate format)
    'P1946': 5,   // Immune Health
    'P11946': 5,  // Immune Health (alternate format)
    'P1948': 7,   // Inflammation
    'P11948': 7,  // Inflammation (alternate format)
    'P1949': 8,   // Bone Health
    'P11949': 8,  // Bone Health (alternate format)
    'P1950': 9,   // Female Hormones
    'P11950': 9,  // Female Hormones (alternate format)
    'P1951': 10,  // Male Hormones
    'P11951': 10, // Male Hormones (alternate format)
    'P1952': 11,  // Sleep and Recovery
    'P11952': 11, // Sleep and Recovery (alternate format)
    // Comprehensive packages
    'P11087': 'ALL' // VITAL INSIGHTS PRO MEN - comprehensive package
  };

  // Helper function to normalize strings for comparison
  const normalizeString = (str) => {
    return str.toLowerCase().replace(/\s+/g, '');
  };

  // Get category by normalized name
  const getCategoryByNormalizedName = (testName) => {
    if (!testName) return null;
    const normalizedTestName = normalizeString(testName);
    
    for (const category of HEALTH_CATEGORIES) {
      if (normalizeString(category.name) === normalizedTestName) {
        return category.id;
      }
    }
    return null;
  };

  const sections = [
    { label: 'Lab Results', component: 'lab_results' },
    { label: 'Generate Report', component: 'generate_report' }
  ];

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        if (appointmentId && isMounted) {
          // Fetch patient data with availableTests
          const patientResponse = await patientService.getPatientById(appointmentId);
          if (!isMounted) return;
          
          const patientData = patientResponse.data?.data || patientResponse.data || patientResponse;
          console.log('📦 Patient data from API:', patientData);
          console.log('📋 Available Tests:', patientData.availableTests);
          
          // Map availableTests to appointmentTests format for compatibility
          if (patientData.availableTests && Array.isArray(patientData.availableTests)) {
            patientData.appointmentTests = patientData.availableTests.map(test => ({
              availableTest: {
                test_code: test.test_code,
                name: test.name
              }
            }));
            console.log('🔄 Mapped to appointmentTests format:', patientData.appointmentTests);
          }
          
          setPatient(patientData);

          try {
            const labResultsResponse = await labResultsService.getLabResultsByAppointmentId(appointmentId);
            if (!isMounted) return;
            
            if (labResultsResponse.success && labResultsResponse.data) {
              setLabResults(labResultsResponse.data);
            }
          } catch (error) {
            console.log('No lab results found or error fetching:', error.message);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        if (isMounted) {
          toast.error('Failed to load patient data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [appointmentId]);

  // Pre-select categories based on patient's appointment tests
  useEffect(() => {
    console.log('🔍 Category Pre-selection Effect Running');
    console.log('Patient data:', patient);
    
    if (patient && patient.appointmentTests && Array.isArray(patient.appointmentTests) && patient.appointmentTests.length > 0) {
      console.log('📋 Appointment Tests:', patient.appointmentTests);
      
      const categoryIds = new Set();
      patient.appointmentTests.forEach(test => {
        let categoryId = null;
        
        // Try matching by test code first
        const testCode = test.availableTest?.test_code;
        console.log('🔎 Checking test code:', testCode);
        
        if (testCode && TEST_CODE_TO_CATEGORY[testCode]) {
          categoryId = TEST_CODE_TO_CATEGORY[testCode];
          console.log('✅ Matched by test code:', testCode, '-> Category ID:', categoryId);
          
          // Handle comprehensive packages that should select all categories
          if (categoryId === 'ALL') {
            console.log('🎯 Comprehensive package detected - selecting all categories');
            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach(id => categoryIds.add(id));
            return; // Skip to next test
          }
        }
        
        // If test code didn't match, try matching by test name
        if (!categoryId) {
          const testName = test.availableTest?.name;
          console.log('🔎 Checking test name:', testName);
          
          if (testName) {
            // Extract category name from test name (e.g., "Blood Mini - Heart Health" -> "Heart Health")
            const parts = testName.split('-');
            if (parts.length > 1) {
              const categoryName = parts[parts.length - 1].trim();
              console.log('📝 Extracted category name:', categoryName);
              categoryId = getCategoryByNormalizedName(categoryName);
              if (categoryId) {
                console.log('✅ Matched by test name:', categoryName, '-> Category ID:', categoryId);
              }
            }
          }
        }
        
        if (categoryId && categoryId !== 'ALL') {
          categoryIds.add(categoryId);
        } else if (!categoryId) {
          console.log('❌ No match found for test:', test.availableTest);
        }
      });
      
      console.log('📊 Final category IDs to select:', Array.from(categoryIds));
      
      if (categoryIds.size > 0) {
        setSelectedCategories(Array.from(categoryIds));
        console.log('✅ Categories pre-selected:', Array.from(categoryIds));
      } else {
        // If no specific categories matched, select all by default
        console.log('⚠️ No categories matched, selecting all categories by default');
        setSelectedCategories([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
      }
    } else {
      console.log('⚠️ No appointment tests found or patient data not ready');
      // If no appointment tests, select all categories by default
      setSelectedCategories([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
    }
  }, [patient]);

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleEditClick = (result) => {
    setEditingId(result.id);
    setEditValue(result.lab_value || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveEdit = async (result) => {
    try {
      const updatedData = {
        appointment_id: appointmentId,
        test_code: result.test_code,
        lab_value: editValue
      };

      await labResultsService.saveLabResult(updatedData);
      
      const labResultsResponse = await labResultsService.getLabResultsByAppointmentId(appointmentId);
      if (labResultsResponse.success && labResultsResponse.data) {
        setLabResults(labResultsResponse.data);
      }
      
      setEditingId(null);
      setEditValue('');
      toast.success('Lab result updated successfully');
    } catch (error) {
      console.error('Error saving lab result:', error);
      toast.error('Failed to update lab result');
    }
  };

  const handleLoadLabResults = async () => {
    setShowSyncConfirmation(false);
    setLoadingLabResults(true);
    try {
      const response = await labResultsService.loadLabResults(appointmentId);
      
      if (response.success) {
        toast.success('Lab results synced successfully!');
        
        // Refresh the lab results
        const labResultsResponse = await labResultsService.getLabResultsByAppointmentId(appointmentId);
        if (labResultsResponse.success && labResultsResponse.data) {
          setLabResults(labResultsResponse.data);
        }
      } else {
        toast.error(response.message || 'Failed to sync lab results');
      }
    } catch (error) {
      console.error('Error syncing lab results:', error);
      toast.error('Failed to sync lab results');
    } finally {
      setLoadingLabResults(false);
    }
  };

  // Helper function to get blood report URL if exists
  const getBloodReportUrl = () => {
    if (!patient?.report_urls || !Array.isArray(patient.report_urls)) {
      return null;
    }
    
    const bloodReport = patient.report_urls.find(
      report => report.report_type === 'bloodReport' && report.report_url
    );
    
    return bloodReport?.report_url || null;
  };

  const handleGenerateReport = async () => {
    if (selectedCategories.length === 0 && !includeAbnormalBiomarkers && !includeResultsAtGlance) {
      toast.warning('Please select at least one category or option');
      return;
    }

    if (!patient?.sukra_order_id) {
      toast.error('Patient order ID not found');
      return;
    }

    setIsNotifying(true);

    try {
      const response = await axios.post(
        'https://vitaldev.vitalinsights.in/content/blood-test-report-notification',
        {
          data: {
            id: patient.id,
            sid_no: patient.sukra_order_id,
            category: selectedCategories.map(id => id.toString()),
            includeAbnormalBiomarkers: includeAbnormalBiomarkers,
            includeResultsAtGlance: includeResultsAtGlance
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNobWFjLXNoYTI1NiIsInR5cCI6IkpXVCJ9'
          },
          timeout: 50000,
          validateStatus: () => true
        }
      );

      const responseData = response.data?.data || response.data;
      
      if (responseData?.status === false) {
        const message = responseData?.message || 'Error generating blood report';
        toast.error(message);
        return;
      }

      toast.success('Blood report generation initiated successfully! This may take 1-2 minutes.');
      
      // Refresh patient data to get updated report_urls
      try {
        const patientResponse = await patientService.getPatientById(appointmentId);
        const patientData = patientResponse.data?.data || patientResponse.data || patientResponse;
        
        // Map availableTests to appointmentTests format for compatibility
        if (patientData.availableTests && Array.isArray(patientData.availableTests)) {
          patientData.appointmentTests = patientData.availableTests.map(test => ({
            availableTest: {
              test_code: test.test_code,
              name: test.name
            }
          }));
        }
        
        setPatient(patientData);
      } catch (error) {
        console.error('Error refreshing patient data:', error);
      }
    } catch (error) {
      console.error('Error generating blood report:', error);
      toast.error('Failed to generate blood report');
    } finally {
      setIsNotifying(false);
    }
  };

  // Helper function to check if lab value should be highlighted in red
  const shouldHighlightLabValue = (labValue) => {
    if (!labValue) return false;
    const value = labValue.toString().trim();
    return value === '<NA>' || value === '*' || value === '®';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Optimal':
        return 'text-green-600 bg-green-50';
      case 'Normal':
        return 'text-blue-600 bg-blue-50';
      case 'Mildly Decreased':
      case 'Mildly Increased':
        return 'text-orange-600 bg-orange-50';
      case 'Significantly Decreased':
      case 'Significantly Increased':
      case 'Abnormal':
      case 'High':
      case 'Low':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const renderLabResultsSection = () => {
    if (labResults.length === 0) {
      return (
        <div className="space-y-4">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowSyncConfirmation(true)}
              disabled={loadingLabResults}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingLabResults ? (
                <>
                  <FaSync className="animate-spin" size={16} />
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <FaSync size={16} />
                  <span>Sync Lab Results</span>
                </>
              )}
            </button>
          </div>
          <div className="text-center py-8 text-gray-500">
            No lab results found for this appointment
          </div>
        </div>
      );
    }

    // Filter lab results based on search term
    let filteredResults = labResults.filter(result => 
      result.test_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.test_code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Separate results with special values and move them to top
    const specialValueResults = filteredResults.filter(result => shouldHighlightLabValue(result.lab_value));
    const normalResults = filteredResults.filter(result => !shouldHighlightLabValue(result.lab_value));
    filteredResults = [...specialValueResults, ...normalResults];

    return (
      <div className="space-y-3">
        {/* Header with Search and Load Button */}
        <div className="flex items-center gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by test name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => setShowSyncConfirmation(true)}
            disabled={loadingLabResults}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {loadingLabResults ? (
              <>
                <FaSync className="animate-spin" size={16} />
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <FaSync size={16} />
                <span>Sync Lab Results</span>
              </>
            )}
          </button>
        </div>

        {/* Scrollable Table Container */}
        <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lab Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Normal Range
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredResults.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No results found matching "{searchTerm}"
                  </td>
                </tr>
              ) : (
                filteredResults.map((result) => (
                <tr key={result.id} className={`${shouldHighlightLabValue(result.lab_value) ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {result.test_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {result.test_code}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingId === result.id ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-24 px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span className={`font-medium ${shouldHighlightLabValue(result.lab_value) ? 'text-red-600' : 'text-gray-900'}`}>
                        {result.lab_value}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {result.normal_value}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(result.result_status)}`}>
                      {result.result_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingId === result.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(result)}
                          className="text-green-600 hover:text-green-800"
                          title="Save"
                        >
                          <FaSave size={20} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          <FaTimes size={20} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditClick(result)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <FaEdit size={20} />
                      </button>
                    )}
                  </td>
                </tr>
              )))
              }
            </tbody>
          </table>
        </div>
        
        {/* Results Count */}
        <div className="text-sm text-gray-600 mt-2">
          Showing {filteredResults.length} of {labResults.length} results
        </div>
      </div>
    );
  };

  const renderGenerateReportSection = () => {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            This will request a blood report from the server. The process may take approximately 1 to 2 minutes.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-blue-900 mb-3">
            Select Health Categories
          </label>
          <div className="space-y-2 max-h-64 overflow-y-auto border border-blue-200 rounded-lg p-3 bg-blue-50">
            {HEALTH_CATEGORIES.map((category) => (
              <label key={category.id} className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
                />
                <span className="text-sm text-gray-700">{category.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2 border border-blue-200 rounded-lg p-3 bg-blue-50">
          <label className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
            <input
              type="checkbox"
              checked={includeAbnormalBiomarkers}
              onChange={(e) => setIncludeAbnormalBiomarkers(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
            />
            <span className="text-sm text-gray-700">Biomarkers of Concern</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
            <input
              type="checkbox"
              checked={includeResultsAtGlance}
              onChange={(e) => setIncludeResultsAtGlance(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
            />
            <span className="text-sm text-gray-700">Results at a Glance</span>
          </label>
        </div>

        <div className="flex justify-end gap-3">
          {getBloodReportUrl() && (
            <a
              href={getBloodReportUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>Show Blood Report</span>
            </a>
          )}
          <button
            onClick={handleGenerateReport}
            disabled={isNotifying}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
          >
            {isNotifying ? (
              <>
                <FaSync size={14} className="animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <span>Generate Report</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 p-4">
      <div className="md:w-64 flex-shrink-0">
        <div className="bg-white p-4 rounded-lg shadow space-y-6">
          {/* Patient Information */}
          {patient && (
            <div className="border-b pb-4">
              <h3 className="text-lg font-bold text-gray-800 mb-3">{patient.profile?.name}</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID:</span>
                  <span className="font-medium">{patient.profile?.sid_no || patient.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Branch:</span>
                  <span className="font-medium">{patient.sukra_branch_id}</span>
                </div>
                {patient.slot_start_time && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Appointment:</span>
                    <span className="font-medium">{patient.slot_start_time?.split("T")[0]}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Report Sections */}
          <div>
            <h3 className="text-xl font-bold text-orange-500 mb-4">Sections</h3>
            <ul className="space-y-2">
              {sections.map((section, index) => (
                <li key={index}>
                  <button
                    onClick={() => setCurrentSection(index)}
                    disabled={submitting}
                    className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                      ? 'bg-orange-500 text-white font-medium' 
                      : 'hover:bg-gray-100'}`}
                  >
                    {section.label}
                    {currentSection === index && (
                      <span className="float-right">→</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      <div className="flex-1">
        <Card>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">
              Lab Results Management
            </h1>
          </div>
          
          <div className="mb-4">
            <h2 className="text-xl font-semibold">
              {sections[currentSection].label}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {currentSection === 0 ? 'View and edit lab results for this patient' : 'Generate blood report with selected categories'}
            </p>
          </div>

          <div className="space-y-6">
            {currentSection === 0 && renderLabResultsSection()}
            {currentSection === 1 && renderGenerateReportSection()}
          </div>

          <div className="flex justify-between mt-6">
            <Button
              variant="secondary"
              onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
            >
              {currentSection > 0 ? 'Previous' : 'Back to List'}
            </Button>

            {currentSection < sections.length - 1 && (
              <Button onClick={goToNextSection}>Next</Button>
            )}
          </div>
        </Card>
      </div>

      {/* Sync Confirmation Modal */}
      {showSyncConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sync Lab Results</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will fetch and sync the latest lab results from the external system. Any existing data will be updated. Do you want to continue?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSyncConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleLoadLabResults}
                disabled={loadingLabResults}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loadingLabResults ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabResultsForm;
